/**
 * Фильтры «Мои заказы» (orders-list-filters; ТЗ 26.10.2026, п.8.1: «Добавить строку с фильтрами: вид
 * услуг, статус заказа, этап заказа»). Статусы — как их видит клиент: «Выполняется» — это «На
 * проверке» и «В работе» сразу. Этап доступен и без услуги (ответ заказчицы 04.11 к п.8.1): тогда
 * в списке этапы всех услуг (`stageFilterOptions`), «Заявка» и «Договор» — общие. Фильтры живут
 * в адресе страницы (`?service=&status=&stage=`): ссылку можно сохранить, «Назад» возвращает к
 * тем же фильтрам.
 */
import { Button } from '@/design-system/components/primitives/button'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import type { OrdersListQuery, Service } from '@/lib/api'
import { SERVICE_LABELS, stageFilterOptions } from '@/lib/orders'

/** Значение «любой» в выпадающих списках (у Radix Select пустого значения нет). */
const ANY = '__any__'

/** Статус фильтра клиента → статусы данных заказа. */
export const CLIENT_STATUS_FILTERS = [
  { value: 'draft', label: 'Черновик', statuses: ['draft'] },
  { value: 'filling', label: 'На заполнении', statuses: ['filling'] },
  { value: 'changes_requested', label: 'Требуются правки', statuses: ['changes_requested'] },
  { value: 'in_progress', label: 'Выполняется', statuses: ['review', 'in_progress'] },
  { value: 'done', label: 'Завершён', statuses: ['done'] },
  { value: 'cancelled', label: 'Отменён', statuses: ['cancelled'] },
] as const

export interface OrderListFilterValue {
  service?: Service
  status?: string
  stage?: string
}

/**
 * Фильтры из адреса страницы: неизвестные значения отбрасываются (старая или испорченная ссылка
 * не ломает список), этап — только этап выбранной услуги. `services` — услуги роли клиента; если
 * услуга одна (физлицо — только обучение), она подразумевается.
 */
export function readOrderListFilters(
  params: URLSearchParams,
  services: Service[],
): OrderListFilterValue {
  const out: OrderListFilterValue = {}
  const service = params.get('service') as Service | null
  if (service && services.includes(service) && services.length > 1) out.service = service
  const status = params.get('status')
  if (status && CLIENT_STATUS_FILTERS.some((s) => s.value === status)) out.status = status
  const stageService = out.service ?? (services.length === 1 ? services[0] : undefined)
  const stage = params.get('stage')
  if (stage && stageFilterOptions(stageService, services).some((s) => s.key === stage)) {
    out.stage = stage
  }
  return out
}

/** Фильтры в адрес страницы (пустые — без параметра). */
export function orderListFilterParams(value: OrderListFilterValue): URLSearchParams {
  const params = new URLSearchParams()
  if (value.service) params.set('service', value.service)
  if (value.status) params.set('status', value.status)
  if (value.stage) params.set('stage', value.stage)
  return params
}

/** Фильтры в запрос списка (`GET /api/orders`). */
export function orderListQuery(
  value: OrderListFilterValue,
  services: Service[],
): Pick<OrdersListQuery, 'service' | 'status' | 'stage'> {
  const service = value.service ?? (services.length === 1 ? services[0] : undefined)
  const status = CLIENT_STATUS_FILTERS.find((s) => s.value === value.status)
  return {
    // Единственная услуга (физлицо) в запрос нужна только вместе с этапом.
    service: value.service ?? (value.stage ? service : undefined),
    status: status ? status.statuses.join(',') : undefined,
    stage: value.stage,
  }
}

export function hasOrderListFilters(value: OrderListFilterValue): boolean {
  return Boolean(value.service || value.status || value.stage)
}

export function OrderListFilters({
  services,
  value,
  onChange,
}: {
  /** Услуги роли клиента; одна — списка «Вид услуги» нет. */
  services: Service[]
  value: OrderListFilterValue
  onChange: (next: OrderListFilterValue) => void
}) {
  const stageService = value.service ?? (services.length === 1 ? services[0] : undefined)
  const stages = stageFilterOptions(stageService, services)
  return (
    <Toolbar aria-label="Фильтры заказов">
      {services.length > 1 ? (
        <Select
          value={value.service ?? ANY}
          // Смена услуги оставляет этап, если он есть и у неё («Договор»), иначе сбрасывает.
          onValueChange={(v) => {
            const service = v === ANY ? undefined : (v as Service)
            const keep =
              value.stage && stageFilterOptions(service, services).some((s) => s.key === value.stage)
            onChange({ ...value, service, stage: keep ? value.stage : undefined })
          }}
        >
          <SelectTrigger className="w-52" aria-label="Вид услуги">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Все услуги</SelectItem>
            {services.map((s) => (
              <SelectItem key={s} value={s}>
                {SERVICE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <Select
        value={value.status ?? ANY}
        onValueChange={(v) => onChange({ ...value, status: v === ANY ? undefined : v })}
      >
        <SelectTrigger className="w-52" aria-label="Статус заказа">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Любой статус</SelectItem>
          {CLIENT_STATUS_FILTERS.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={value.stage ?? ANY}
        onValueChange={(v) => onChange({ ...value, stage: v === ANY ? undefined : v })}
      >
        <SelectTrigger className="w-60" aria-label="Этап заказа">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Любой этап</SelectItem>
          {stages.map((s) => (
            <SelectItem key={s.key} value={s.key}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasOrderListFilters(value) ? (
        <Button variant="secondary" size="sm" onClick={() => onChange({})}>
          Сбросить фильтры
        </Button>
      ) : null}
    </Toolbar>
  )
}
